import { useQuery } from "@tanstack/react-query";
import {
  loadLocationDetails,
  syncAccessibility,
  syncAccessibilityEnterprise,
} from "../../../apis/venueDetails";
import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
  AccessibilityFilters,
  BusinessNote,
  BusinessNoteAction,
  BusinessNoteTask,
  businessNotesAll,
  ComplexCategoriesData,
  VenueDetails,
} from "../../../types";

type UseVenueDetailsBySlugProps = {
  slug: string;
  /** Optional; omitted or empty when location is not needed (e.g. business portal). */
  latitude?: string;
  longitude?: string;
  accessToken: string;
  enabled: boolean;
  type?: "Basic" | "Complex";
  syncSpaces?: boolean;
  password?: string;
  passwordToken?: string;
  ssoToken?: string;
};

export type VenueDetailsBySlugResponse = {
  details: VenueDetails;
  accesibilityCategories?: AccessibilityCategory[]; // Only for Basic venues
  accesibilityCategoriesHybrid?: AccessibilityCategoryHybrid[]; // Deprecated - hybrid venues now use Enterprise data
  accesibilityCategoriesEnterprise?: ComplexCategoriesData[]; // For Complex (Hybrid + Enterprise) venues
  floorTypeOptions?: { id: number; title: string }[]; // For Complex venues
  enterpriseBusinessNotesActions?: BusinessNoteAction[]; // For Complex venues
  businessNotesTasks?: {
    Custom: BusinessNoteTask[];
    General: BusinessNoteTask[];
  }; // For Complex venues
  review_tags?: ComplexCategoriesData[]; // For Complex venues
  businessNotesAreaLocationTypes?: string[]; // For Complex venues
  businessNotesForMappers?: BusinessNote[]; // For Complex venues
  businessNotesAll?: businessNotesAll[]; // For Complex venues
  accessibility_filters?: AccessibilityFilters; // For Complex venues
  canViewPage?: number;
  passwordProtectedPageToken?: string;
  isSsoProtect?: number;
  ssoTenantId?: string;
};

export function useVenueDetailsBySlug({
  slug,
  latitude = "",
  longitude = "",
  accessToken,
  enabled,
  type,
  syncSpaces = true,
  password,
  passwordToken,
  ssoToken,
}: UseVenueDetailsBySlugProps) {
  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "loadVenueDetailsBySlug",
      slug,
      latitude,
      longitude,
      accessToken,
      type,
      syncSpaces,
      password,
      passwordToken,
      ssoToken,
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    queryFn: async () => {
      const detailsResponse = await loadLocationDetails(slug, {
        latitude,
        longitude,
        token: accessToken,
        type,
      });

      const isComplexVenue = detailsResponse.templateType !== "Basic";

      // Skip sync-spaces when the caller explicitly opts out (e.g. whats-nearby).
      if (!syncSpaces) {
        return { details: detailsResponse } as VenueDetailsBySlugResponse;
      }

      if (isComplexVenue) {
        // Both Hybrid and Enterprise venues use sync-spaces.
        // can_view_page is returned by sync-spaces (not by the details API),
        // so we always call it and check access from the result.
        const syncResult = await syncAccessibilityEnterprise(
          detailsResponse.business.id,
          slug,
          { password, passwordToken, ssoToken },
        );

        // Access denied — return just enough for the password/SSO gate to render.
        if (syncResult.canViewPage === 0) {
          return {
            details: detailsResponse,
            canViewPage: 0,
            isSsoProtect: syncResult.isSsoProtect,
            ssoTenantId: syncResult.ssoTenantId,
          } as VenueDetailsBySlugResponse;
        }

        return {
          details: detailsResponse,
          accesibilityCategoriesEnterprise: syncResult.accesibilityCategoriesEnterprise,
          floorTypeOptions: syncResult.floorTypeOptions,
          enterpriseBusinessNotesActions: syncResult.enterpriseBusinessNotesActions,
          businessNotesTasks: syncResult.businessNotesTasks,
          review_tags: syncResult.review_tags,
          businessNotesAreaLocationTypes: syncResult.businessNotesAreaLocationTypes,
          businessNotesForMappers: syncResult.businessNotesForMappers,
          businessNotesAll: syncResult.businessNotesAll,
          accessibility_filters: syncResult.accessibility_filters,
          canViewPage: syncResult.canViewPage,
          passwordProtectedPageToken: syncResult.passwordProtectedPageToken,
          isSsoProtect: syncResult.isSsoProtect,
          ssoTenantId: syncResult.ssoTenantId,
        } as VenueDetailsBySlugResponse;
      } else {
        // Basic venues — no password protection
        const accesibilityCategories = await syncAccessibility(
          detailsResponse.business.id,
          slug,
        );
        return {
          details: detailsResponse,
          accesibilityCategories,
        } as VenueDetailsBySlugResponse;
      }
    },
    enabled,
  });
  return {
    data,
    error,
    isLoading: isLoading || isFetching,
    refetch,
  };
}
